/**
 * @fileoverview React hook for Meta Ads Connect operations.
 * @module meta-ads-connect/hooks/useMetaAdsConnect
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createMetaAdsConnectService, type MetaAdsConnectService } from '../services/MetaAdsConnectService';
import type {
  MetaAdsAdAccountSummary,
  MetaAdsConnectionStatus,
  MetaAdsDeliverySyncResponse,
  MetaAdsInsightsPreviewResponse,
  MetaAdsMetadataPreviewResponse,
  MetaAdsMetadataSyncResponse,
  MetaAdsSelectAdAccountsResponse,
} from '../types';

function normalizeMetaAdAccountId(adAccountId: string): string {
  const trimmed = adAccountId.trim();
  if (trimmed.startsWith('act_')) return trimmed;
  return `act_${trimmed.replace(/^act_/i, '')}`;
}

const OAUTH_SESSION_KEY = 'meta_ads_oauth_in_progress';

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export interface MetaAdsPreviewParams {
  adAccountId: string;
  since: string;
  until: string;
  limit?: number;
}

export interface MetaAdsMetadataPreviewParams {
  adAccountId: string;
  limit?: number;
  maxPages?: number;
  namePrefix?: string;
}

export interface UseMetaAdsConnectResult {
  status: MetaAdsConnectionStatus | null;
  adAccounts: MetaAdsAdAccountSummary[];
  preview: MetaAdsInsightsPreviewResponse | null;
  metadataPreview: MetaAdsMetadataPreviewResponse | null;
  syncResult: MetaAdsDeliverySyncResponse | null;
  metadataSyncResult: MetaAdsMetadataSyncResponse | null;
  isConnected: boolean;
  loading: boolean;
  actionLoading: boolean;
  accountsLoading: boolean;
  previewLoading: boolean;
  metadataPreviewLoading: boolean;
  syncLoading: boolean;
  metadataSyncLoading: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  refreshAdAccounts: () => Promise<void>;
  loadInsightsPreview: (params: MetaAdsPreviewParams) => Promise<void>;
  loadMetadataPreview: (params: MetaAdsMetadataPreviewParams) => Promise<void>;
  syncDelivery: (params: MetaAdsPreviewParams) => Promise<void>;
  syncMetadata: (params: MetaAdsMetadataPreviewParams) => Promise<void>;
  clearPreview: () => void;
  /** G1: persist the explicit sync/report ad-account selection (AC-G1-1). */
  saveAdAccountSelection: (adAccountIds: string[]) => Promise<MetaAdsSelectAdAccountsResponse>;
  selectionSaving: boolean;
}

export function useMetaAdsConnect(
  projectId: string,
  serviceOverride?: Pick<
    MetaAdsConnectService,
    | 'getOAuthUrl' | 'getStatus' | 'disconnect' | 'listAdAccounts'
    | 'getInsightsPreview' | 'getMetadataPreview' | 'syncDelivery'
    | 'syncMetadata' | 'selectAdAccounts'
  >,
): UseMetaAdsConnectResult {
  const [status, setStatus] = useState<MetaAdsConnectionStatus | null>(null);
  const [adAccounts, setAdAccounts] = useState<MetaAdsAdAccountSummary[]>([]);
  const [preview, setPreview] = useState<MetaAdsInsightsPreviewResponse | null>(null);
  const [metadataPreview, setMetadataPreview] = useState<MetaAdsMetadataPreviewResponse | null>(null);
  const [syncResult, setSyncResult] = useState<MetaAdsDeliverySyncResponse | null>(null);
  const [metadataSyncResult, setMetadataSyncResult] = useState<MetaAdsMetadataSyncResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [metadataPreviewLoading, setMetadataPreviewLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [metadataSyncLoading, setMetadataSyncLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statusAbortRef = useRef<AbortController | null>(null);
  const processedCallbackRef = useRef(false);
  const mountedRef = useRef(true);

  const service = useMemo(
    () => serviceOverride ?? createMetaAdsConnectService(),
    [serviceOverride],
  );
  const [selectionSaving, setSelectionSaving] = useState(false);

  const refreshStatus = useCallback(async () => {
    if (!projectId) return;

    statusAbortRef.current?.abort();
    statusAbortRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      const nextStatus = await service.getStatus(projectId, statusAbortRef.current.signal);
      if (mountedRef.current) {
        setStatus(nextStatus);
        setAdAccounts(nextStatus.accessibleAdAccounts || []);
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load Meta Ads connection status');
        setStatus({ connected: false });
        setAdAccounts([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [projectId, service]);

  const connect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const oauthUrl = await service.getOAuthUrl(projectId, returnTo);
      sessionStorage.setItem(OAUTH_SESSION_KEY, projectId);
      window.location.href = oauthUrl;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to connect Meta Ads');
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const disconnect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      setPreview(null);
      setMetadataPreview(null);
      setSyncResult(null);
      const nextStatus = await service.disconnect(projectId);
      if (mountedRef.current) {
        setStatus(nextStatus);
        setAdAccounts([]);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to disconnect Meta Ads');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const refreshAdAccounts = useCallback(async () => {
    if (!projectId) return;

    try {
      setAccountsLoading(true);
      setError(null);
      const result = await service.listAdAccounts(projectId);
      if (mountedRef.current) {
        setAdAccounts(result.accounts);
        setStatus(prev => prev ? { ...prev, accessibleAdAccounts: result.accounts } : prev);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to refresh Meta Ads ad accounts');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setAccountsLoading(false);
      }
    }
  }, [projectId, service]);

  const loadInsightsPreview = useCallback(async (params: MetaAdsPreviewParams) => {
    if (!projectId) return;

    try {
      setPreviewLoading(true);
      setError(null);
      const result = await service.getInsightsPreview({
        projectId,
        ...params,
      });
      if (mountedRef.current) {
        setPreview(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to preview Meta Ads insights');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setPreviewLoading(false);
      }
    }
  }, [projectId, service]);

  const loadMetadataPreview = useCallback(async (params: MetaAdsMetadataPreviewParams) => {
    if (!projectId) return;

    try {
      setMetadataPreviewLoading(true);
      setError(null);
      const result = await service.getMetadataPreview({
        projectId,
        ...params,
      });
      if (mountedRef.current) {
        setMetadataPreview(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to preview Meta Ads metadata');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setMetadataPreviewLoading(false);
      }
    }
  }, [projectId, service]);

  const syncDelivery = useCallback(async (params: MetaAdsPreviewParams) => {
    if (!projectId) return;

    try {
      setSyncLoading(true);
      setError(null);
      const result = await service.syncDelivery({
        projectId,
        ...params,
      });
      if (mountedRef.current) {
        setSyncResult(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to sync Meta Ads delivery');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setSyncLoading(false);
      }
    }
  }, [projectId, service]);

  const syncMetadata = useCallback(async (params: MetaAdsMetadataPreviewParams) => {
    if (!projectId) return;

    try {
      setMetadataSyncLoading(true);
      setError(null);
      const result = await service.syncMetadata({
        projectId,
        ...params,
      });
      if (mountedRef.current) {
        setMetadataSyncResult(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to sync Meta Ads metadata');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setMetadataSyncLoading(false);
      }
    }
  }, [projectId, service]);

  const clearPreview = useCallback(() => {
    setPreview(null);
    setMetadataPreview(null);
    setSyncResult(null);
    setMetadataSyncResult(null);
  }, []);

  // G1 (AC-G1-1): save the explicit selection, then verify the STORED
  // readback matches what was requested (Google Ads saveSelection parity —
  // success is claimed only after the server truth agrees).
  const saveAdAccountSelection = useCallback(async (adAccountIds: string[]) => {
    if (!projectId) throw new Error('projectId is required');
    setSelectionSaving(true);
    try {
      const result = await service.selectAdAccounts(projectId, adAccountIds);
      const requested = [...new Set(adAccountIds.map(normalizeMetaAdAccountId))].sort();
      const stored = [...(result.selectedAdAccountIds ?? [])].sort();
      if (JSON.stringify(requested) !== JSON.stringify(stored)) {
        throw new Error('Meta Ads selection readback did not match the saved accounts. Please retry.');
      }
      const nextStatus = await service.getStatus(projectId);
      if (mountedRef.current) {
        setStatus(nextStatus);
        setAdAccounts(nextStatus.accessibleAdAccounts || []);
      }
      return result;
    } finally {
      if (mountedRef.current) setSelectionSaving(false);
    }
  }, [projectId, service]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      statusAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (processedCallbackRef.current || !projectId) return;

    const params = new URLSearchParams(window.location.search);
    const callbackState = params.get('metaAds');
    const callbackError = params.get('error');
    const oauthProjectId = sessionStorage.getItem(OAUTH_SESSION_KEY);

    if (!callbackState) return;
    if (oauthProjectId && oauthProjectId !== projectId) return;

    if (callbackState === 'connected') {
      processedCallbackRef.current = true;
      sessionStorage.removeItem(OAUTH_SESSION_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      refreshStatus();
      return;
    }

    if (callbackState === 'error' || callbackError) {
      processedCallbackRef.current = true;
      sessionStorage.removeItem(OAUTH_SESSION_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      setError(callbackError ? decodeURIComponent(callbackError) : 'Meta Ads OAuth failed');
    }
  }, [projectId, refreshStatus]);

  return {
    status,
    adAccounts,
    preview,
    metadataPreview,
    syncResult,
    metadataSyncResult,
    isConnected: status?.connected ?? false,
    loading,
    actionLoading,
    accountsLoading,
    previewLoading,
    metadataPreviewLoading,
    syncLoading,
    metadataSyncLoading,
    error,
    refreshStatus,
    connect,
    disconnect,
    refreshAdAccounts,
    loadInsightsPreview,
    loadMetadataPreview,
    syncDelivery,
    syncMetadata,
    clearPreview,
    saveAdAccountSelection,
    selectionSaving,
  };
}

export default useMetaAdsConnect;
